import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Breadcrumb,
    Button,
    Container,
    Divider,
    Dropdown,
    Header,
    Icon,
    Label,
    Segment,
    Table
} from 'semantic-ui-react';
import axios from 'axios';

import CommandConfig from '../components/CommandConfig';
import DeleteCommand from '../components/DeleteCommand';
import { useAuth, useError } from '../providers';

const Custom = () => {
    const [commands, setCommands] = useState({});
    const [filtered, setFiltered] = useState({});
    const [filterType, setFilterType] = useState(null);
    const [refresh, setRefresh] = useState(0);

    const { showError } = useError();
    const { user } = useAuth();

    const filter = (ev, data, def) => {
        if (data === null) {
            setFiltered(def ? def : commands);
            setFilterType(null);
        } else {
            setFiltered((def ? def : commands).filter(data.filter))
            setFilterType(data)
        }
    }

    const tagOptions = [
        {
            key: 'Enabled',
            text: 'Enabled',
            value: 'Enabled',
            filter: (el) => {
                return el.flags & 1
            },
            label: { color: 'green', empty: true, circular: true },
        },
        {
            key: 'Disabled',
            text: 'Disabled',
            value: 'Disabled',
            filter: (el) => {
                return !(el.flags & 1)
            },
            label: { color: 'red', empty: true, circular: true },
        },
        {
            key: 'Sub Only',
            text: 'Sub Only',
            value: 'Sub Only',
            filter: (el) => {
                return el.flags & 2
            },
            label: { color: 'grey', empty: true, circular: true },
        },
        {
            key: 'Alias',
            text: 'Alias',
            value: 'Alias',
            filter: (el) => {
                return el.is_alias
            },
            label: { color: 'orange', empty: true, circular: true },
        },
        {
            key: 'None',
            text: 'None',
            value: 'None',
            filter: (el) => {
                return true
            },
            label: { empty: true, circular: true },
        },
    ]

    if (user.admin) {
        tagOptions.push({
            key: 'Global',
            text: 'Global',
            value: 'Global',
            filter: (el) => {
                return el.type === 'global'
            },
            label: { color: 'pink', empty: true, circular: true },
        })
        tagOptions.push({
            key: 'Not Global',
            text: 'Not Global',
            value: 'Not Global',
            filter: (el) => {
                return el.type !== 'global'
            },
            label: { color: 'pink', empty: true, circular: true },
        })
    }

    useEffect(() => {
        const getCommands = async () => {
            try {
                const { data } = await axios(
                    `/api/custom`,
                );
                if (data.status === 200) {
                    setCommands(data.response.commands || []);
                    filter(null, filterType, data.response.commands)
                }
            } catch (err) {
                showError(<center>Oops, something went wrong. Don't worry, we've been notified of the issue and will fix it as soon as possible!</center>);
            }
        }

        if (user['username']) {
            getCommands();
        }
        // eslint-disable-next-line
    }, [user, refresh, showError]);

    const refreshCommands = () => {
        setRefresh(value => value + 1);
    }

    return (
        <Segment className='lobby' inverted>
            <Container text className='description'>
                <Breadcrumb size='huge'>
                    <Breadcrumb.Section link as={Link} to='/dashboard'>Dashboard</Breadcrumb.Section>
                    <Breadcrumb.Divider icon='right chevron' />
                    <Breadcrumb.Section active>Custom Commands</Breadcrumb.Section>
                </Breadcrumb>
                <Divider hidden />
                <p>
                    <Button floated='right' icon size='small' labelPosition='left' as={Link} to='/dashboard/custom/create'>
                        <Icon name='plus' />
                        Create Custom Command
                    </Button>
                    Create and manage custom commands for Twitching of Isaac.
                </p>
            </Container>
            <br />
            <Table celled compact definition inverted>
                <Table.Header fullWidth>
                    <Table.Row>
                        <Table.HeaderCell colspan={2}>
                            Command
                            <div style={{ float: "right" }}>
                                <Dropdown
                                    text={(filterType === null ? 'Filter' : 'Filtered: ' + filterType.text)}
                                    icon='filter'
                                    floating
                                    labeled
                                    button
                                    selection
                                    mini
                                    className='icon'
                                >
                                    <Dropdown.Menu>
                                        <Dropdown.Menu scrolling>
                                            {tagOptions.map((option) => (
                                                <Dropdown.Item onClick={filter} key={option.value} {...option} />
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        </Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {Object.values(filtered).length === 0 ?
                        <Table.Row>
                            <Table.Cell colSpan={2}>
                                <Header textAlign='center' style={{ 'color': 'white' }}>
                                    {(filterType !== '') ? 'No commands found matching your selected filter.' : 'You don\'t have any custom commands defined yet...'}
                                </Header>
                            </Table.Cell>
                        </Table.Row>
                        :
                        Object.values(filtered).map((command, index) =>
                            <Table.Row key={index}>
                                <Table.Cell style={{ "position": "relative" }}>
                                    <strong>
                                        !{command.command}
                                        {Object.values(command.params).map((param, index) => <em>&nbsp;[{param.name}]</em>)}
                                    </strong>
                                    <br />
                                    {command.description}
                                    <div style={{
                                        "position": "absolute",
                                        "right": "5px",
                                        "top": "5px",
                                    }}>
                                        {command.flags === 0 ? <React.Fragment><Label color='red' size='mini'>Disabled</Label></React.Fragment> : ''}
                                        {command.flags & 1 ? <React.Fragment><Label color='green' size='mini'>Enabled</Label></React.Fragment> : ''}
                                        {command.flags & 2 ? <React.Fragment><Label color='grey' size='mini'>Sub Only</Label></React.Fragment> : ''}
                                        {command.is_alias ? <React.Fragment><Label color='orange' size='mini'>Alias</Label></React.Fragment> : ''}
                                        {command.type === 'global' ? <React.Fragment><Label color='pink' size='mini'>Global</Label></React.Fragment> : ''}
                                    </div>
                                </Table.Cell>
                                <Table.Cell collapsing>
                                    <CommandConfig command={command} refresh={refreshCommands} type={command.type} />
                                    <Button icon='edit' as={Link} to={`/dashboard/custom/edit/${command.command}`} />
                                    <DeleteCommand command={command} refresh={refreshCommands} />
                                </Table.Cell>
                            </Table.Row>
                        )
                    }
                </Table.Body>
            </Table>
        </Segment >
    )
};

export default Custom;
